// Telegram CloudStorage (account-scoped, synced across devices) mirrored to
// localStorage. Values are small (< 4 KB seed ciphertext) so no chunking.
//
// Robustness: some Telegram clients never fire the CloudStorage callback, which
// would hang wallet creation. So every read/write also uses localStorage, and
// CloudStorage calls are bounded by a timeout — the flow never blocks on it.

const cs = () => window.Telegram?.WebApp?.CloudStorage;
const TIMEOUT_MS = 4000;

export const hasCloud = () => !!cs();

// Telegram CloudStorage accepts keys of 1–128 chars from [A-Za-z0-9_-] only. An
// invalid key (e.g. one containing ':') is silently rejected — the callback
// never fires — so a bad key means a permanently lost write. Guard loudly.
const VALID_KEY = /^[A-Za-z0-9_-]{1,128}$/;
function assertKey(key: string) {
  if (!VALID_KEY.test(key)) {
    throw new Error(`invalid CloudStorage key "${key}" (allowed: A-Za-z0-9_-, 1-128 chars)`);
  }
}

function localGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function localSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}
function localRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

function withTimeout<T>(p: Promise<T>, fallback: T): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((res) => setTimeout(() => res(fallback), TIMEOUT_MS)),
  ]);
}

/** Read the localStorage mirror first (instant — the seed ciphertext is
 *  immutable, so the local copy is authoritative), then CloudStorage for a new
 *  device that has no local copy yet. */
export async function csGet(key: string): Promise<string | null> {
  assertKey(key);
  const local = localGet(key);
  if (local != null) return local;
  const c = cs();
  if (c) {
    return withTimeout(
      new Promise<string | null>((res) => c.getItem(key, (e, val) => res(e ? null : (val ?? null)))),
      null,
    );
  }
  return null;
}

/** Write to the localStorage mirror immediately, then CloudStorage. Returns true
 *  only when CloudStorage confirmed the write (the durable, cross-device store) —
 *  callers persisting a seed must treat `false` as "not safely stored". */
export async function csSet(key: string, value: string): Promise<boolean> {
  assertKey(key);
  localSet(key, value);
  const c = cs();
  if (!c) return false;
  return withTimeout(
    new Promise<boolean>((res) => c.setItem(key, value, (err, ok) => res(!err && ok !== false))),
    false,
  );
}

/** Diagnostic: report where (if anywhere) a key can be found, distinguishing a
 *  lost write from a hung CloudStorage read. Never throws. */
export async function csDiag(key: string): Promise<string> {
  const local = localGet(key);
  const c = cs();
  if (!c) return `cloud=none local=${local ? 'hit' : 'miss'}`;
  const started = performance.now();
  const cloud = await withTimeout(
    new Promise<{ v: string | null; err: string | null }>((res) =>
      c.getItem(key, (e, val) => res({ v: e ? null : (val ?? null), err: e ? String(e) : null })),
    ),
    { v: null, err: 'timeout' },
  );
  const ms = Math.round(performance.now() - started);
  return `cloud=${cloud.err ?? (cloud.v ? 'hit' : 'miss')}(${ms}ms) local=${local ? 'hit' : 'miss'}`;
}

export async function csRemove(key: string): Promise<void> {
  assertKey(key);
  localRemove(key);
  const c = cs();
  if (!c) return;
  await withTimeout(new Promise<void>((res) => c.removeItem(key, () => res())), undefined);
}
